import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';
import { HabitSchedule } from 'shared/types/habit-schedule';
import { TaskType } from 'shared/types/task-base';
import { WeekDays } from 'shared/types/week-days';
import { getActiveSchedule } from 'shared/utils/get-active-schedule';
import { getScheduleLabel } from 'shared/utils/get-schedule-label';

import * as Styled from './task-meta-interaction-repeat.style';

export type TaskMetaInteractionRepeatProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value: HabitSchedule[] | null;
  type: TaskType;
  onChange: (changes: {
    type: TaskType;
    schedules: HabitSchedule[] | null;
  }) => void;
  weekStartsOn: WeekDays;
  targetAllowed: boolean;
  changeToHabitAllowed: boolean;
  onPremium: () => void;
};

export const TaskMetaInteractionRepeat: React.FC<
  TaskMetaInteractionRepeatProps
> = ({
  value,
  type,
  onChange,
  weekStartsOn,
  targetAllowed,
  changeToHabitAllowed,
  onPremium,

  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [adjustedType, setAdjustedType] = useState<
    TaskType.Habit | TaskType.Repeating
  >(
    type === TaskType.Task
      ? changeToHabitAllowed
        ? TaskType.Habit
        : TaskType.Repeating
      : type,
  );
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const val = getActiveSchedule(value);

  const _onChangeType = (type: TaskType.Habit | TaskType.Repeating) => {
    if (type === TaskType.Habit && !changeToHabitAllowed) {
      onPremium();
      return;
    }

    setAdjustedType(type);
  };

  const _clear = () => onChange({ type: TaskType.Task, schedules: null });

  const _onChange = (schedule: HabitSchedule | null) => {
    if (!schedule) {
      _clear();
      closeMenu();
      return;
    }

    const endedSchedules = value?.filter(({ endDate }) => !!endDate) ?? [];

    onChange({
      type: adjustedType,
      schedules: [...endedSchedules, schedule],
    });
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={<Icon icon={Repeat} />}
        color={val ? Colors.TextPrimary : Colors.Inherit}
        selected={menuOpen}
        onClick={openMenu}
        label={val ? getScheduleLabel(val, t) : undefined}
        tooltipLabel={t(
          val
            ? 'task.repeat.tooltip.edit.label'
            : 'task.repeat.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <PopupMenu
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
          onOutside={closeMenu}
        >
          <RepeatForm
            initialRrule={val?.rrule?.format}
            initialFrequency={val?.frequency}
            weekStartsOn={weekStartsOn}
            type={adjustedType}
            onChange={_onChange}
            onChangeType={_onChangeType}
            isTargetPremium={!targetAllowed}
            onPremium={onPremium}
            isCreateHabitPremium={!changeToHabitAllowed}
            showTypeToggle
          />
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
